<template>
  <div>
    <!-- Solo mostrar el formulario si hay un producto seleccionado o se está usando como modal -->
    <div v-if="shouldShowRecipe">
      <CRow>
        <CCol sm="12">
          <CCard>
            <CCardHeader v-if="!showModal">
              <strong>{{ isEditing ? 'Editar' : 'Crear' }} Receta</strong>
                <div class="card-header-actions">
                  <CButton 
                    v-if="!hasExistingRecipe"
                    color="primary" 
                    size="sm" 
                    @click="resetForm"
                    class="mr-2"
                  >
                    Nueva Receta
                  </CButton>
                <CButton 
                  color="secondary" 
                  size="sm" 
                  @click="$emit('cancel')"
                  class="mr-2"
                >
                  Cancelar
                </CButton>
                <CButton 
                  color="success" 
                  size="sm" 
                  @click="handleSaveClick"
                  :disabled="false"
                  ref="saveButton"
                  data-testid="save-recipe-button"
                >
                  {{ isEditing ? 'Actualizar' : 'Guardar' }} Receta
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <!-- Columna izquierda: Información básica -->
                <CCol md="6">
                  <CCard>
                    <CCardHeader>
                      <strong>Información Básica</strong>
                    </CCardHeader>
                    <CCardBody>
                      <CRow>
                        <CCol md="12">
                          <CInput
                            label="Producto"
                            :value="selectedProductName"
                            placeholder="Producto seleccionado"
                            readonly
                            class="cursor-default"
                          />
                        </CCol>
                      </CRow>
                      
                      <CRow>
                        <CCol md="6">
                          <CInput
                            label="Cantidad que produce"
                            :value="1"
                            readonly
                            disabled
                            class="cursor-default"
                          />
                        </CCol>
                      </CRow>

                      <CRow>
                        <CCol md="12">
                          <CTextarea
                            label="Instrucciones de preparación"
                            v-model="form.instructions"
                            rows="4"
                            placeholder="Describe los pasos para preparar este producto..."
                          />
                        </CCol>
                      </CRow>

                      <!-- Resumen de costos por presentación -->
                      <CCard class="mt-3">
                        <CCardHeader>
                          <strong>Resumen de Costos por Presentación</strong>
                        </CCardHeader>
                        <CCardBody>
                          <CRow>
                            <CCol md="3" class="mb-2">
                              <strong>Personal:</strong>
                              <div class="text-success">S/ {{ formatCost(costsByPresentation.personal?.cost_presentation) }}</div>
                            </CCol>
                            <CCol md="3" class="mb-2">
                              <strong>Mediana:</strong>
                              <div class="text-success">S/ {{ formatCost(costsByPresentation.mediana?.cost_presentation) }}</div>
                            </CCol>
                            <CCol md="3" class="mb-2">
                              <strong>Grande:</strong>
                              <div class="text-success">S/ {{ formatCost(costsByPresentation.grande?.cost_presentation) }}</div>
                            </CCol>
                            <CCol md="3" class="mb-2">
                              <strong>Familiar:</strong>
                              <div class="text-success">S/ {{ formatCost(costsByPresentation.familiar?.cost_presentation) }}</div>
                            </CCol>
                          </CRow>
                        </CCardBody>
                      </CCard>
                    </CCardBody>
                  </CCard>
                </CCol>

                <!-- Columna derecha: Ingredientes -->
                <CCol md="6">
                  <!-- Ingredientes de preparación -->
                  <CCard class="mb-3">
                    <CCardHeader>
                      <strong>Ingredientes</strong>
                      <div class="card-header-actions">
                        <CButton 
                          v-if="ingredients.length > 0 && !showIngredientsDropdown"
                          color="info" 
                          size="sm" 
                          @click="showIngredientsDropdown = true"
                          class="mr-2"
                        >
                          <i class="fa fa-chevron-down mr-1"></i>
                          Ver Ingredientes ({{ ingredients.length }})
                        </CButton>
                        <CButton 
                          v-if="showIngredientsDropdown"
                          color="secondary" 
                          size="sm" 
                          @click="showIngredientsDropdown = false"
                          class="mr-2"
                        >
                          <i class="fa fa-chevron-up mr-1"></i>
                          Ocultar Ingredientes
                        </CButton>
                        <CButton 
                          color="primary" 
                          size="sm" 
                          @click="addIngredient"
                        >
                          Agregar Ingrediente
                        </CButton>
                      </div>
                    </CCardHeader>
                    <CCardBody>
                      <!-- Mensaje cuando ingredientes están colapsados -->
                      <div v-if="ingredients.length > 0 && !showIngredientsDropdown" class="mb-3">
                        <div class="alert alert-info text-center py-2">
                          <i class="fa fa-info-circle mr-1"></i>
                          Hay {{ ingredients.length }} ingrediente{{ ingredients.length > 1 ? 's' : '' }} agregado{{ ingredients.length > 1 ? 's' : '' }}. 
                          <strong>Haz clic en "Ver Ingredientes"</strong> para ver los detalles.
                        </div>
                      </div>
                      
                      <!-- Dropdown de ingredientes existentes -->
                      <div v-if="ingredients.length > 0 && showIngredientsDropdown" class="mb-3">
                        <div class="border rounded p-3 bg-light">
                          <h6 class="mb-3">Ingredientes Agregados ({{ ingredients.length }})</h6>
                          <div class="row">
                            <div 
                              v-for="(ingredient, index) in ingredients" 
                              :key="ingredient.id || 'ingredient-' + index"
                              class="col-md-6 mb-2"
                            >
                              <div class="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{{ getSupplyName(ingredient.supply_id) }}</strong>
                                  <br>
                                  <small class="text-muted">
                                    {{ getUnitName(ingredient.unit_id) }} - 
                                    S/ {{ ingredient.price_cost || 0 }}
                                  </small>
                                </div>
                                <CButton 
                                  color="danger" 
                                  size="sm"
                                  @click="removeIngredient(index)"
                                >
                                  <i class="fa fa-trash"></i>
                                </CButton>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Formulario de ingredientes (siempre visible para agregar nuevos) -->
                      <div v-if="ingredients.length == 0" class="text-muted text-center py-3">
                        <i class="fa fa-plus-circle fa-2x mb-2"></i>
                        <br>
                        No hay ingredientes agregados
                        <br>
                        <small>Haz clic en "Agregar Ingrediente" para comenzar</small>
                      </div>
                      
                      <!-- Formularios detallados de ingredientes - SOLO visibles cuando el dropdown está expandido -->
                      <div v-else-if="showIngredientsDropdown">
                        <div 
                          v-for="(ingredient, index) in ingredients" 
                          :key="ingredient.id || 'ingredient-' + index"
                          class="ingredient-row mb-3 p-3 border rounded bg-light"
                        >
                          <!-- Fila principal con campos principales -->
                          <CRow class="mb-3">
                            <CCol md="12" class="mb-2">
                              <label class="form-label">Insumo/Producto</label>
                              <select
                                v-model="ingredient.supply_id"
                                class="form-control"
                                @change="updateIngredientPrice(ingredient, index)"
                              >
                                <option value="">Seleccionar insumo o producto...</option>
                                <option 
                                  v-for="supply in supplies" 
                                  :key="supply.id" 
                                  :value="supply.id"
                                >
                                  {{ supply.name }} ({{ supply.type == 'product' ? 'Producto' : 'Insumo' }})
                                </option>
                              </select>
                            </CCol>
                            <CCol md="12" class="mb-2">
                              <label class="form-label">Unidad</label>
                              <select
                                v-model="ingredient.unit_id"
                                class="form-control"
                              >
                                <option value="">Seleccionar unidad...</option>
                                <option 
                                  v-for="unit in units" 
                                  :key="unit.id" 
                                  :value="unit.id"
                                >
                                  {{ unit.name }}
                                </option>
                              </select>
                            </CCol>
                            <!-- Campo de merma oculto pero mantiene funcionalidad -->
                            <CInput
                              v-model.number="ingredient.merma"
                              type="hidden"
                              :value="0"
                            />
                          </CRow>
                          
                          <!-- Cantidades por presentación -->
                          <CRow class="mb-3">
                            <CCol md="12">
                              <label class="form-label"><strong>Cantidades por Presentación</strong></label>
                            </CCol>
                            <CCol md="3" class="mb-2">
                              <label class="form-label">Personal</label>
                              <CInput
                                v-model="ingredient.quantities.personal"
                                type="text"
                                placeholder="Ej: 1, 1/2"
                                @input="() => calculateIngredientCost(ingredient, index)"
                                :title="'Cantidad para presentación personal'"
                              />
                            </CCol>
                            <CCol md="3" class="mb-2">
                              <label class="form-label">Mediana</label>
                              <CInput
                                v-model="ingredient.quantities.mediana"
                                type="text"
                                placeholder="Ej: 2, 1.5"
                                @input="() => calculateIngredientCost(ingredient, index)"
                                :title="'Cantidad para presentación mediana'"
                              />
                            </CCol>
                            <CCol md="3" class="mb-2">
                              <label class="form-label">Grande</label>
                              <CInput
                                v-model="ingredient.quantities.grande"
                                type="text"
                                placeholder="Ej: 3, 2.5"
                                @input="() => calculateIngredientCost(ingredient, index)"
                                :title="'Cantidad para presentación grande'"
                              />
                            </CCol>
                            <CCol md="3" class="mb-2">
                              <label class="form-label">Familiar</label>
                              <CInput
                                v-model="ingredient.quantities.familiar"
                                type="text"
                                placeholder="Ej: 4, 3.5"
                                @input="() => calculateIngredientCost(ingredient, index)"
                                :title="'Cantidad para presentación familiar'"
                              />
                            </CCol>
                            <CCol md="12" class="mb-2">
                              <CButton 
                                color="danger" 
                                size="sm"
                                @click="removeIngredient(index)"
                                class="w-100"
                              >
                                Eliminar Ingrediente
                              </CButton>
                            </CCol>
                          </CRow>
                          
                          <!-- Fila de información de costos -->
                          <CRow class="border-top pt-3">
                            <CCol md="6" class="mb-2">
                              <label class="form-label">Precio Unitario</label>
                              <div class="form-control-plaintext">
                                S/ {{ ingredient.price_purchase || 0 }}
                              </div>
                            </CCol>
                            <CCol md="6" class="mb-2">
                              <label class="form-label">Costo Total</label>
                              <div class="form-control-plaintext">
                                S/ {{ ingredient.price_cost || 0 }}
                              </div>
                            </CCol>
                          </CRow>
                        </div>
                        <div class="mt-3 p-3 bg-light border rounded">
                          <div class="row align-items-center">
                            <div class="col">
                              <strong>Subtotal Ingredientes:</strong>
                            </div>
                            <div class="col-auto">
                              <span class="h5 mb-0">S/ {{ subtotalIngredientes }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Mensaje cuando hay ingredientes pero el dropdown está colapsado -->
                      <div v-else-if="ingredients.length > 0 && !showIngredientsDropdown" class="text-center py-4">
                        <div class="alert alert-success">
                          <i class="fa fa-check-circle fa-2x mb-2"></i>
                          <br>
                          <strong>{{ ingredients.length }} ingrediente{{ ingredients.length > 1 ? 's' : '' }} configurado{{ ingredients.length > 1 ? 's' : '' }}</strong>
                          <br>
                          <small>Haz clic en "Ver Ingredientes" arriba para editarlos</small>
                          <br>
                          <div class="mt-2">
                            <strong>Subtotal: S/ {{ subtotalIngredientes }}</strong>
                          </div>
                        </div>
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <!-- Modal para seleccionar tipo de presentación -->
      <CModal
        :show.sync="showPresentationModal"
        title="Seleccionar Tipo de Presentación"
        size="lg"
        color="primary"
        :backdrop="true"
        :closeOnBackdrop="true"
        :centered="true"
      >
        <template #header>
          <h4 class="modal-title">Seleccionar Tipo de Presentación</h4>
          <button type="button" class="btn-close" @click="showPresentationModal = false" aria-label="Close"></button>
        </template>
        
        <template #default>
          <div class="list-group">
            <button
              v-for="presentation in predefinedPresentations"
              :key="presentation.id"
              type="button"
              class="list-group-item list-group-item-action"
              @click="selectPresentation(presentation)"
            >
              <div class="d-flex w-100 justify-content-between">
                <h6 class="mb-1">{{ presentation.name }}</h6>
                <small class="text-muted">{{ presentation.size }}</small>
              </div>
            </button>
          </div>
        </template>
        
        <template #footer>
          <CButton color="secondary" @click="showPresentationModal = false">
            Cancelar
          </CButton>
        </template>
      </CModal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'Recipe',
  components: {
    // Los componentes de CoreUI se registran globalmente
  },
  props: {
    productId: {
      type: [Number, String, null],
      default: null
    },
    recipeId: {
      type: [Number, String, null], 
      default: null
    },
    selectedProduct: {
      type: Object,
      default: null
    },
    selectedRecipe: {
      type: Object,
      default: null
    },
    selectedPrice: {
      type: Object,
      default: null
    },
    showModal: {
      type: Boolean,
      default: false
    },
    availablePrices: {
      type: Array,
      default: () => []
    },
    campu_id:{
      type: String,
      default: "1"
    }
  },
  data() {
    return {
      isEditing: false,
      isSaving: false,
      isCreatingNew: false,
      showProductModal: false,
      localSelectedProduct: null, // Local copy to avoid prop mutation
      form: {
        id: null,
        user_id: null,
        product_id: "",
        quantity: 1, // Siempre fijo en 1
        product_unit_id: "",
        instructions: "",
        cost_price: 0,
        margin_value: 0,
        cost_total: 0,
        cost_presentation: 0,
        state: 1
      },
      ingredients: [],
      products: [],
      supplies: [],
      units: [],
      presentations: [],
      selectedPriceId: null,
      costsByPresentation: {
        personal: { cost_presentation: 0 },
        mediana: { cost_presentation: 0 },
        grande: { cost_presentation: 0 },
        familiar: { cost_presentation: 0 }
      },
      selectedPresentation: null,
      selectedPresentationName: '',
      showPresentationModal: false,
      predefinedPresentations: [
        { id: 'personal', name: 'Personal', size: 'Pequeño' },
        { id: 'mediano', name: 'Mediano', size: 'Mediano' },
        { id: 'grande', name: 'Grande', size: 'Grande' },
        { id: 'familiar', name: 'Familiar', size: 'Extra Grande' }
      ],
      productFields: [
        { key: 'name', label: 'Producto' },
        { key: 'category.name', label: 'Categoría' },
        { key: 'product_unit', label: 'Unidades' }
      ],
      calculateTimeout: null,
      showIngredientsDropdown: false // Control para mostrar/ocultar ingredientes existentes
    }
  },
  watch: {
    selectedProduct: {
      async handler(newProduct) {
        if (newProduct) {
          this.localSelectedProduct = newProduct
          this.form.product_id = newProduct.id || newProduct.Id
          
          // Solo cargar datos iniciales si no están cargados
          if (this.supplies.length == 0) {
            await this.loadInitialData()
          }
          
          // Solo cargar recetas existentes si no hay una receta específica seleccionada para editar
          if (!this.selectedRecipe || !this.selectedRecipe.id) {
            await this.loadExistingRecipes(newProduct.id || newProduct.Id)
          }
        }
      },
      immediate: true
    },
    selectedRecipe: {
      async handler(newRecipe) {
        if (newRecipe && newRecipe.id) {
          this.isCreatingNew = false
          await this.loadRecipe(newRecipe.id)
        } else if (newRecipe == null) {
          this.isCreatingNew = true
          this.resetForm()
        }
      },
      immediate: true
    },
    selectedPrice: {
      handler(newPrice) {
        if (newPrice) {
          this.selectedPriceId = newPrice.id
        }
      },
      immediate: true
    },
    productId: {
      handler(newProductId) {
        if (newProductId && this.supplies.length == 0) {
          this.loadInitialData().then(() => {
            this.preselectProduct()
          })
        } else if (newProductId) {
          this.preselectProduct()
        }
      },
      immediate: true
    },
    ingredients: {
      handler(newIngredients) {
        // Solo calcular totales, no hacer llamadas HTTP automáticamente
        this.calculateTotals()
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters(['get_all_user', 'get_company']),
    shouldShowRecipe() {
      return this.selectedProduct || this.productId || this.showModal || this.recipeId
    },
    selectedProductName() {
      const product = this.localSelectedProduct || this.selectedProduct
      return product ? product.name : 'No hay producto seleccionado'
    },
    subtotalIngredientes() {
      return this.ingredients.reduce((total, ingredient) => {
        return total + parseFloat(ingredient.price_cost || 0)
      }, 0).toFixed(2)
    },
    hasExistingRecipe() {
      // Verificar si hay una receta existente
      return this.isEditing || 
             (this.form.id != null && this.form.id != undefined) ||
             (this.recipeId != null && this.recipeId != undefined) ||
             (this.ingredients.length > 0 && this.form.product_id) ||
             (this.form.product_id && this.existingRecipes.length > 0)
    }
    },
    async mounted() {
    await this.loadInitialData()
    
    if (this.recipeId) {
      await this.loadRecipe(this.recipeId)
    }
    
    // FORZAR HABILITACIÓN DEL BOTÓN CADA SEGUNDO
    setInterval(() => {
      // Buscar por múltiples selectores
      let button = document.querySelector('[data-testid="save-recipe-button"]')
      
      if (!button) {
        // Buscar por texto "Guardar Receta"
        const buttons = document.querySelectorAll('button')
        button = Array.from(buttons).find(btn => 
          btn.textContent && btn.textContent.includes('Guardar Receta')
        )
      }
      
      if (!button) {
        // Buscar por clase btn-success
        button = document.querySelector('button.btn-success')
      }
      
      if (button) {
        // FORZAR HABILITACIÓN COMPLETA
        button.disabled = false
        button.removeAttribute('disabled')
        button.style.setProperty('opacity', '1', 'important')
        button.style.setProperty('cursor', 'pointer', 'important')
        button.style.setProperty('pointer-events', 'auto', 'important')
        button.style.setProperty('background-color', '#28a745', 'important')
        button.style.setProperty('border-color', '#28a745', 'important')
        button.style.setProperty('color', 'white', 'important')
        button.style.setProperty('display', 'inline-block', 'important')
        button.style.setProperty('text-decoration', 'none', 'important')
        button.style.setProperty('user-select', 'auto', 'important')
        button.style.setProperty('touch-action', 'manipulation', 'important')
        button.style.setProperty('outline', 'none', 'important')
        button.style.setProperty('box-shadow', '0 2px 4px rgba(0,0,0,0.1)', 'important')
        button.style.setProperty('filter', 'none', 'important')
        button.style.setProperty('transform', 'none', 'important')
        
        // REMOVER TODAS LAS CLASES PROBLEMÁTICAS
        button.classList.remove('disabled', 'btn-disabled', 'btn-secondary', 'btn-outline-secondary')
        button.classList.add('btn-success', 'btn-enabled')
        
        // FORZAR ATRIBUTOS
        button.setAttribute('aria-disabled', 'false')
        button.setAttribute('tabindex', '0')
        
        // AGREGAR EVENT LISTENER DIRECTO SI NO EXISTE
        if (!button.hasAttribute('data-click-handler-added')) {
          button.addEventListener('click', (e) => {
            this.handleSaveClick(e)
          })
          button.setAttribute('data-click-handler-added', 'true')
        }
      }
    }, 500) // Reducido a 500ms para mayor frecuencia
    
    // MÉTODO GLOBAL PARA FORZAR HABILITACIÓN DESDE CONSOLA
    window.forceEnableButton = () => {
      const buttons = document.querySelectorAll('button')
      const button = Array.from(buttons).find(btn => 
        btn.textContent && btn.textContent.includes('Guardar Receta')
      )
      
      if (button) {
        button.disabled = false
        button.removeAttribute('disabled')
        button.style.setProperty('opacity', '1', 'important')
        button.style.setProperty('cursor', 'pointer', 'important')
        button.style.setProperty('pointer-events', 'auto', 'important')
        button.style.setProperty('background-color', '#28a745', 'important')
        button.style.setProperty('border-color', '#28a745', 'important')
        button.style.setProperty('color', 'white', 'important')
        button.classList.remove('disabled', 'btn-disabled')
        button.classList.add('btn-success')
        
        return button
      } else {
        return null
      }
    }
  },
  
  beforeDestroy() {
    // Limpiar timeout para evitar memory leaks
    if (this.calculateTimeout) {
      clearTimeout(this.calculateTimeout)
    }
  },
  
  methods: {
    ...mapActions(['get', 'post']),
    
    async loadInitialData() {
      try {
        const response = await this.get({
          url: `${this.$store.getters.get__url}/recipes/initialData/${this.campu_id}`,
          token: this.$store.getters.get__token
        })
        
        if (response && response.error == false) {
          this.products = response.data.products || []
          this.supplies = response.data.supplies || []
          this.units = response.data.units || []
          
          if (this.productId && this.products.length > 0) {
            await this.preselectProduct()
          }
        }
        
        // Cargar presentaciones
        await this.loadPresentations()
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error)
      }
    },

    async loadPresentations() {
      try {
        const response = await this.get({
          url: `${this.$store.getters.get__url}/recipes/presentations`,
          token: this.$store.getters.get__token
        })
        
        if (response && response.error == false) {
          this.presentations = response.data || []
        }
      } catch (error) {
        console.error('Error al cargar presentaciones:', error)
        // Usar presentaciones por defecto si falla la carga
        this.presentations = [
          { value: 'personal', label: 'Personal' },
          { value: 'mediana', label: 'Mediana' },
          { value: 'grande', label: 'Grande' },
          { value: 'familiar', label: 'Familiar' }
        ]
      }
    },

    async loadExistingRecipes(productId) {
      try {
        // Solo cargar recetas existentes si no hay una receta específica seleccionada
        if (this.selectedRecipe && this.selectedRecipe.id) {
          return
        }
        
        // Si se está creando una nueva receta, no cargar automáticamente recetas existentes
        if (this.isCreatingNew) {
          return
        }
        const response = await this.get({
          url: `${this.$store.getters.get__url}/recipes/byProduct/${productId}`,
          token: this.$store.getters.get__token
        })
        
        
        if (response.data && response.data.length > 0) {
          // Si hay recetas existentes, cargar la primera para edición
          const existingRecipe = response.data[0]
          await this.loadRecipe(existingRecipe.id)
          // Asegurar que el dropdown esté colapsado al cargar receta existente
          this.showIngredientsDropdown = false
        } else {
          // Si no hay recetas, limpiar el formulario para crear una nueva
          this.resetForm()
        }
      } catch (error) {
        console.error('Error loading existing recipes:', error)
        this.resetForm()
      }
    },

    resetForm() {
      this.isEditing = false
      this.isCreatingNew = true
      this.form = {
        id: null,
        user_id: null,
        product_id: (this.localSelectedProduct || this.selectedProduct) ? ((this.localSelectedProduct || this.selectedProduct).id || (this.localSelectedProduct || this.selectedProduct).Id) : null,
        product_unit_id: null,
        quantity: 1, // Siempre fijo en 1
        instructions: '',
        cost_price: 0,
        margin_value: 0,
        cost_total: 0,
        cost_presentation: 0,
        state: 1
      }
      this.ingredients = []
      this.editingIngredientIndex = -1
      this.selectedPresentation = null
      this.selectedPresentationName = ''
      this.showIngredientsDropdown = false
      
      // Resetear costos por presentación
      this.costsByPresentation = {
        personal: { cost_presentation: 0 },
        mediana: { cost_presentation: 0 },
        grande: { cost_presentation: 0 },
        familiar: { cost_presentation: 0 }
      }
    },

    async loadRecipe(recipeId) {
      try {
        const baseUrl = this.$store.getters.get__url
        const fullUrl = `${baseUrl}/recipes/${recipeId}/show`
        const token = this.$store.getters.get__token
        
        const request = {
          url: fullUrl,
          token: token
        }
        const response = await this.$store.dispatch('get', request)
        
        if (response && response.error == false && response.data) {
          const recipe = response.data
          
          // Populate form with recipe data
          this.form = {
            id: recipe.id,
            user_id: recipe.user_id,
            product_id: recipe.product_id,
            product_unit_id: recipe.product_unit_id,
            quantity: 1, // Siempre fijo en 1, ignorar el valor de la BD
            instructions: recipe.instructions || '',
            cost_price: recipe.cost_price || 0,
            margin_value: recipe.margin_value || 0,
            cost_total: recipe.cost_total || 0,
            cost_presentation: recipe.cost_presentation || 0,
            state: recipe.state
          }
          
          // Asegurarse de que los supplies estén cargados antes de procesar ingredientes
          if (this.supplies.length == 0) {
            await this.loadInitialData()
          }
          
          // Agrupar ingredientes por insumo y presentación
          this.ingredients = this.groupIngredientsBySupply(recipe.details || [])
          
          // Al cargar receta existente, mantener el dropdown colapsado
          this.showIngredientsDropdown = false
          
          // Cargar costos por presentación
          await this.loadCostsByPresentation(recipe.id)
          
          // Calcular costos en tiempo real con los datos cargados
          this.calculateTotals()
          
          // Find and set the product
          const product = this.products.find(p => p.id == recipe.product_id)
          if (product) {
            this.localSelectedProduct = product
          }
          
          this.isEditing = true
        } else {
          console.error('Invalid response format:', response)
          this.$toast.error('Error al cargar la receta: Respuesta inválida')
        }
      } catch (error) {
        console.error('Error loading recipe:', error)
        this.$toast.error('Error al cargar la receta: ' + error.message)
      }
    },
    
    async preselectProduct() {
      try {
        if (!this.productId) {
          return
        }
        
        await this.$nextTick()
        
        let product = this.products.find(p => {
          return p.id == this.productId || p.Id == this.productId
        })
        
        if (!product && this.products.length == 0) {
          await new Promise(resolve => setTimeout(resolve, 500))
          product = this.products.find(p => p.id == this.productId || p.Id == this.productId)
        }
        
        if (!product) {
          product = this.products.find(p => p.id == parseInt(this.productId) || p.Id == parseInt(this.productId))
        }
        
        if (product) {
          this.selectProduct(product)
        } else {
          console.warn('Producto no encontrado con ID:', this.productId)
        }
      } catch (error) {
      }
    },
    
    async selectProduct(product) {
      this.localSelectedProduct = product
      this.form.product_id = product.id || product.Id
      
      if (product.product_unit && product.product_unit.length > 0) {
        this.form.product_unit_id = product.product_unit[0].id
      } else {
        const productId = product.id || product.Id
        const units = await this.loadProductUnits(productId)
        if (units && units.length > 0) {
          this.form.product_unit_id = units[0].id
        }
      }
      
      this.showProductModal = false
      this.$forceUpdate()
    },

    async loadProductUnits(productId) {
      try {
        const response = await this.get({
          url: `${this.$store.getters.get__url}/purchases/byUnit/${productId}`,
          token: this.$store.getters.get__token
        })
        
        if (response && response.error == false) {
          const units = response.data.data || []
          
          const product = this.localSelectedProduct || this.selectedProduct
          if (product && (product.id == productId || product.Id == productId)) {
            if (this.localSelectedProduct) {
              this.localSelectedProduct.product_unit = units
            }
          }
          
          return units
        } else {
          return []
        }
      } catch (error) {
        return []
      }
    },

    addIngredient() {
      this.ingredients.push({
        id: Date.now() + Math.random(), // ID único para evitar claves duplicadas
        supply_id: '',
        unit_id: '',
        unit_type: '',
        type: 'supply', // Tipo por defecto
        // Cantidades por presentación
        quantities: {
          personal: '',
          mediana: '',
          grande: '',
          familiar: ''
        },
        merma: 0, // Siempre 0 ya que no se usa merma
        price_purchase: 0,
        price_real: 0,
        equivalence: 0,
        price_cost: 0
      })
    },

    removeIngredient(index) {
      this.ingredients.splice(index, 1)
      this.calculateTotals()
    },

     async updateIngredientPrice(ingredient, index) {
       if (ingredient.supply_id && ingredient.supply_id != '') {
         const supply = this.supplies.find(s => s.id == ingredient.supply_id)
         if (supply) {
           // Determinar el tipo basado en el ID
           if (supply.id.startsWith('product_')) {
             ingredient.type = 'product'
             // Usar el precio que ya viene en supply.price_cost si está disponible
             ingredient.price_purchase = supply.price_cost || 0
             
             // Solo hacer llamada HTTP si no tenemos el precio y es necesario
             if (!supply.price_cost && supply.original_id) {
               try {
                 const productId = supply.original_id || supply.id.replace('product_', '')
                 const response = await this.get({
                   url: `${this.$store.getters.get__url}/recipes/product-purchase-price/${productId}`,
                   token: this.$store.getters.get__token
                 })
                 if (response && response.error == false && response.data) {
                   ingredient.price_purchase = response.data.purchase_price || 0
                 }
               } catch (error) {
                 console.error('Error obteniendo precio del producto:', error)
               }
             }
           } else if (supply.id.startsWith('supply_')) {
             ingredient.type = 'supply'
             ingredient.price_purchase = supply.price_cost || 0
           }
           
           if (!ingredient.unit_id && supply.unit && supply.unit.id) {
             ingredient.unit_id = supply.unit.id
           }
           
           this.calculateIngredientCost(ingredient, index)
         }
       }
     },

    groupIngredientsBySupply(details) {
      // Agrupar ingredientes por supply_id o product_id
      const grouped = {}
      
      details.forEach(detail => {
        // Determinar si es un insumo o producto
        const isProduct = detail.product_id != null && detail.product_id != undefined
        const supplyId = isProduct ? `product_${detail.product_id}` : `supply_${detail.supply_id}`
        const key = `${supplyId}_${detail.unit_id}`
        
        if (!grouped[key]) {
          grouped[key] = {
            id: Date.now() + Math.random(), // ID único
            supply_id: supplyId, // Usar el ID con prefijo para que coincida con la lista de supplies
            unit_id: detail.unit_id,
            unit_type: detail.unit_type || '',
            type: isProduct ? 'product' : 'supply',
            quantities: {
              personal: '',
              mediana: '',
              grande: '',
              familiar: ''
            },
            merma: detail.merma || 0,
            price_purchase: detail.price_purchase || 0,
            price_real: detail.price_real || 0,
            equivalence: detail.equivalence || 0,
            price_cost: 0
          }
        }
        
        // Asignar cantidad a la presentación correspondiente
        if (detail.presentation && grouped[key].quantities.hasOwnProperty(detail.presentation)) {
          grouped[key].quantities[detail.presentation] = detail.quantity
        }
      })
      
          // Calcular costo total para cada ingrediente agrupado
          Object.values(grouped).forEach(ingredient => {
            let totalQuantity = 0
            const presentations = ['personal', 'mediana', 'grande', 'familiar']
            
            presentations.forEach(presentation => {
              const quantity = ingredient.quantities[presentation]
              if (quantity && quantity != '') {
                totalQuantity += parseFloat(quantity)
              }
            })
            
            // El price_cost es el total de todas las presentaciones usando price_real
            ingredient.price_cost = (totalQuantity * ingredient.price_real).toFixed(2)
          })
      
      return Object.values(grouped)
    },

    async loadCostsByPresentation(recipeId) {
      try {
        const baseUrl = this.$store.getters.get__url
        const fullUrl = `${baseUrl}/recipes/${recipeId}/costs-by-presentation`
        const token = this.$store.getters.get__token
        
        const request = {
          url: fullUrl,
          token: token
        }
        
        const response = await this.$store.dispatch('get', request)
        
        if (response && response.error == false && response.data) {
          this.costsByPresentation = response.data
        }
      } catch (error) {
        console.error('Error loading costs by presentation:', error)
      }
    },

    calculateIngredientCost(ingredient, index) {
      if (ingredient.supply_id) {
        if (!ingredient.price_purchase || ingredient.price_purchase == 0) {
          const supply = this.supplies.find(s => s.id == ingredient.supply_id)
          if (supply) {
            ingredient.price_purchase = supply.price_cost || 0
          }
        }
        
        const merma = parseFloat(ingredient.merma || 0)
        ingredient.price_real = ingredient.price_purchase * (1 + merma / 100)
        
        // Calcular costo total basado en las cantidades de todas las presentaciones
        let totalQuantity = 0
        const presentations = ['personal', 'mediana', 'grande', 'familiar']
        
        presentations.forEach(presentation => {
          const quantity = ingredient.quantities[presentation]
          if (quantity && quantity != '') {
            totalQuantity += this.parseFraction(quantity)
          }
        })
        
        // El price_cost es el total de todas las presentaciones usando price_real
        ingredient.price_cost = (totalQuantity * ingredient.price_real).toFixed(2)
        
        this.$forceUpdate()
        this.calculateTotals()
      }
    },

    calculateTotals() {
      // Debounce para evitar cálculos excesivos
      if (this.calculateTimeout) {
        clearTimeout(this.calculateTimeout)
      }
      
      this.calculateTimeout = setTimeout(() => {
        const totalIngredientCost = parseFloat(this.subtotalIngredientes)
        this.form.cost_price = totalIngredientCost.toFixed(2)
        
        // Ya no se usa margen de error
        this.form.margin_value = 0
        
        const quantity = 1 // Siempre fijo en 1
        this.form.cost_total = totalIngredientCost.toFixed(2) // Costo total = costo precio (sin margen)
        
        this.form.cost_presentation = (parseFloat(this.form.cost_total) / quantity).toFixed(2)
        
        // Calcular costos por presentación en tiempo real
        this.calculateCostsByPresentation()
      }, 300) // 300ms de debounce
    },

    calculateCostsByPresentation() {
      const presentations = ['personal', 'mediana', 'grande', 'familiar']
      
      presentations.forEach(presentation => {
        let costPrice = 0
        
        // Calcular costo de ingredientes para esta presentación
        this.ingredients.forEach(ingredient => {
          if (ingredient.supply_id && ingredient.unit_id) {
            const presentationQuantity = ingredient.quantities[presentation]
            if (presentationQuantity && presentationQuantity != '') {
              const quantity = this.parseFraction(presentationQuantity)
              // Usar price_real que ya incluye la merma
              const priceReal = parseFloat(ingredient.price_real || 0)
              costPrice += quantity * priceReal
            }
          }
        })
        
        // Ya no se usa margen de error
        const marginValue = 0
        const costTotal = costPrice // Costo total = costo precio (sin margen)
        // El costo por presentación es el costo total de esa presentación
        const costPresentation = costTotal
        
        this.costsByPresentation[presentation] = {
          cost_price: costPrice,
          margin_value: marginValue,
          cost_total: costTotal,
          cost_presentation: costPresentation
        }
      })
    },

    formatCost(cost) {
      if (!cost || cost == 0) return '0.00'
      return parseFloat(cost).toFixed(2)
    },

    parseFraction(input) {
      if (typeof input == 'number') return input
      
      const str = input.toString().trim()
      
      const fractionMatch = str.match(/^(\d+)\/(\d+)$/)
      if (fractionMatch) {
        const numerator = parseInt(fractionMatch[1])
        const denominator = parseInt(fractionMatch[2])
        return numerator / denominator
      }
      
      const mixedMatch = str.match(/^(\d+)\s+(\d+)\/(\d+)$/)
      if (mixedMatch) {
        const whole = parseInt(mixedMatch[1])
        const numerator = parseInt(mixedMatch[2])
        const denominator = parseInt(mixedMatch[3])
        return whole + (numerator / denominator)
      }
      
      return parseFloat(str) || 0
    },

    handleSaveClick(event) {
      if (this.isSaving) {
        return
      }
      
      if (event) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
      }
      
      this.saveRecipe()
    },

    async saveRecipe() {
      if (this.isSaving) {
        return
      }
      
      this.isSaving = true
      
      try {
        let productId = this.form.product_id || this.productId
        
        if (!productId && (this.localSelectedProduct || this.selectedProduct)) {
          const product = this.localSelectedProduct || this.selectedProduct
          productId = product.Id || product.id
        }
        
        if (!productId) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon: 'error',
            title: 'Error: No se pudo determinar el producto'
          })
          this.isSaving = false
          return
        }
        
        if (!this.form.product_unit_id && !this.selectedPresentation?.product_unit_id) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon: 'error',
            title: 'Error: Debe seleccionar una unidad de producto'
          })
          this.isSaving = false
          return
        }

        // Crear registros de ingredientes por presentación
        const ingredientsForDetails = []
        
        this.ingredients
          .filter(ingredient => {
            return ingredient.supply_id && ingredient.supply_id != '' && 
                   ingredient.unit_id && ingredient.unit_id != ''
          })
          .forEach(ingredient => {
            // Para cada presentación que tenga cantidad, crear un registro
            const presentations = ['personal', 'mediana', 'grande', 'familiar']
            
            presentations.forEach(presentation => {
              const quantity = ingredient.quantities[presentation]
              if (quantity && quantity != '' && parseFloat(quantity) > 0) {
                ingredientsForDetails.push({
                  supply_id: ingredient.supply_id, // Enviar el ID con prefijo tal como está
                  unit_id: parseInt(ingredient.unit_id),
                  quantity: parseFloat(quantity) || 0,
                  presentation: presentation,
                  merma: parseFloat(ingredient.merma) || 0,
                  price_purchase: parseFloat(ingredient.price_purchase) || 0,
                  price_real: parseFloat(ingredient.price_real) || 0,
                  equivalence: parseFloat(ingredient.equivalence) || 0,
                  price_cost: parseFloat(ingredient.price_cost) || 0
                })
              }
            })
          })

        if (ingredientsForDetails.length == 0) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon: 'error',
            title: 'Error: Debe agregar al menos un ingrediente con cantidades para alguna presentación'
          })
          this.isSaving = false
          return
        }

        const payload = {
          id: this.isEditing ? this.form.id : null,
          product_id: parseInt(productId),
          product_unit_id: parseInt(this.form.product_unit_id || this.selectedPresentation?.product_unit_id),
          quantity: 1, // Siempre fijo en 1
          instructions: this.form.instructions || "",
          ingredients: ingredientsForDetails
        }

        const baseUrl = this.$store.getters.get__url
        const endpoint = this.isEditing ? 'recipes/update' : 'recipes/store'
        const fullUrl = `${baseUrl}/${endpoint}`
        const token = this.$store.getters.get__token
        
        const request = {
          url: fullUrl,
          params: payload,
          token: token
        }
        
        const response = await this.$store.dispatch('post', request)

        if (response && typeof response == 'object') {
          // Crear una copia para evitar modificaciones
          const responseCopy = { ...response }
          
          const statusCode = responseCopy.statusCode || responseCopy.status || 0
          const hasError = (responseCopy && responseCopy.error == true) || false
          const message = responseCopy.message || ''
          
          if (statusCode == 200 && !hasError) {
            if (message.includes('Ya existe una receta')) {
              Swal.fire({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                icon: 'warning',
                title: 'Ya existe una receta para este producto'
              })
              this.isSaving = false
              return
            }
            
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              icon: 'success',
              title: this.isEditing ? 'Receta actualizada correctamente' : 'Receta creada correctamente'
            })
            
            // Cargar costos por presentación después de guardar
            if (responseCopy.data && responseCopy.data.id) {
              await this.loadCostsByPresentation(responseCopy.data.id)
            }
            
            this.$emit('recipe-saved', responseCopy.data)
            
            if (!this.productId && !this.recipeId) {
              this.resetForm()
            }
            
            return
          } else if (responseCopy.data && responseCopy.data.state == 1) {
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              icon: 'warning',
              title: 'Ya existe una receta para este producto'
            })
            this.isSaving = false
            return
          } else {
            Swal.fire({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              icon: 'warning',
              title: 'Respuesta inesperada del servidor'
            })
            this.isSaving = false
            return
          }
        } else {
          Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon: 'error',
            title: 'Error: Respuesta inválida del servidor'
          })
          this.isSaving = false
          return
        }
      } catch (error) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          icon: 'error',
          title: 'Error al guardar la receta: ' + (error.message || 'Error desconocido')
        })
      } finally {
        this.isSaving = false
      }
    },

    resetForm() {
      this.isEditing = false
      this.form = {
        id: null,
        user_id: null,
        product_id: "",
        quantity: 1, // Siempre fijo en 1
        product_unit_id: "",
        instructions: "",
        cost_price: 0,
        margin_value: 0,
        cost_total: 0,
        cost_presentation: 0,
        state: 1
      }
      this.ingredients = []
      this.selectedPriceId = null
      this.selectedPresentation = null
      this.selectedPresentationName = ''
      this.showIngredientsDropdown = false
      
      // Resetear costos por presentación
      this.costsByPresentation = {
        personal: { cost_presentation: 0 },
        mediana: { cost_presentation: 0 },
        grande: { cost_presentation: 0 },
        familiar: { cost_presentation: 0 }
      }
    },

    async selectPresentation(presentation) {
      let productUnitId = null
      const product = this.localSelectedProduct || this.selectedProduct
      const productId = product ? (product.id || product.Id) : null
      
      if (productId) {
        if (product.product_unit && product.product_unit.length > 0) {
          productUnitId = product.product_unit[0].id
        } else {
          const productFromList = this.products.find(p => (p.id || p.Id) == productId)
          
          if (productFromList && productFromList.product_unit && productFromList.product_unit.length > 0) {
            productUnitId = productFromList.product_unit[0].id
          } else {
            productUnitId = 1 // Valor por defecto temporal
          }
        }
      }
      
      if (!productUnitId) {
        productUnitId = 1 // Valor por defecto temporal
      }
      
      this.selectedPresentation = {
        ...presentation,
        product_unit_id: productUnitId
      }
      this.selectedPresentationName = presentation.name
      this.showPresentationModal = false
      this.form.product_unit_id = productUnitId
    },

    // Métodos helper para obtener nombres
    getSupplyName(supplyId) {
      if (!supplyId) return 'N/A'
      const supply = this.supplies.find(s => s.id == supplyId)
      return supply ? supply.name : 'N/A'
    },

    getUnitName(unitId) {
      if (!unitId) return 'N/A'
      const unit = this.units.find(u => u.id == unitId)
      return unit ? unit.name : 'N/A'
    }
  }
}
</script>

<style scoped>
.ingredient-row {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  transition: background-color 0.2s ease;
}

.ingredient-row:hover {
  background-color: #e9ecef;
}

.card-header-actions {
  display: flex;
  gap: 8px;
}

.text-success { color: #28a745 !important; }
.text-warning { color: #ffc107 !important; }
.text-primary { color: #007bff !important; }
.text-danger { color: #dc3545 !important; }

.cursor-pointer {
  cursor: pointer !important;
}

.cursor-default {
  cursor: default !important;
}

/* Modo oscuro */
.c-dark-theme .ingredient-row {
  background-color: #343a40 !important;
  border: 1px solid #495057 !important;
  color: #f8f9fa !important;
}

.c-dark-theme .ingredient-row:hover {
  background-color: #495057 !important;
}

.c-dark-theme .form-control {
  background-color: #495057 !important;
  border-color: #6c757d !important;
  color: #f8f9fa !important;
}

.c-dark-theme .form-control:focus {
  background-color: #495057 !important;
  border-color: #80bdff !important;
  color: #f8f9fa !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
}

.c-dark-theme .card {
  background-color: #343a40 !important;
  border-color: #495057 !important;
  color: #f8f9fa !important;
}

.c-dark-theme .card-header {
  background-color: #495057 !important;
  border-color: #6c757d !important;
  color: #f8f9fa !important;
}

.c-dark-theme .text-muted {
  color: #adb5bd !important;
}

.c-dark-theme .text-success { 
  color: #40e0d0 !important; 
}

.c-dark-theme .text-warning { 
  color: #ffd700 !important; 
}

.c-dark-theme .text-primary { 
  color: #66b3ff !important; 
}

.c-dark-theme .text-danger { 
  color: #ff6b6b !important; 
}

/* Estilos para el dropdown de ingredientes */
.ingredients-dropdown {
  transition: all 0.3s ease;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.c-dark-theme .alert-info {
  background-color: #1a4a4e !important;
  border-color: #2a5a5e !important;
  color: #a8d4da !important;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.c-dark-theme .alert-success {
  background-color: #1a4a3e !important;
  border-color: #2a5a4e !important;
  color: #a8d4aa !important;
}

/* FORZAR BOTÓN SIEMPRE HABILITADO - ULTRA AGRESIVO */
button[data-testid="save-recipe-button"],
button[data-testid="save-recipe-button"]:disabled,
button[data-testid="save-recipe-button"].disabled,
button[data-testid="save-recipe-button"][disabled="disabled"],
button[data-testid="save-recipe-button"][disabled="true"],
button[data-testid="save-recipe-button"][disabled],
.btn[data-testid="save-recipe-button"],
.btn[data-testid="save-recipe-button"]:disabled,
.btn[data-testid="save-recipe-button"].disabled,
.btn[data-testid="save-recipe-button"][disabled="disabled"],
.btn[data-testid="save-recipe-button"][disabled="true"],
.btn[data-testid="save-recipe-button"][disabled] {
  opacity: 1 !important;
  cursor: pointer !important;
  pointer-events: auto !important;
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  color: white !important;
  display: inline-block !important;
  text-decoration: none !important;
  user-select: auto !important;
  touch-action: manipulation !important;
  outline: none !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
  filter: none !important;
  transform: none !important;
  transition: all 0.15s ease-in-out !important;
}

button[data-testid="save-recipe-button"]:hover,
button[data-testid="save-recipe-button"]:disabled:hover,
button[data-testid="save-recipe-button"].disabled:hover,
.btn[data-testid="save-recipe-button"]:hover,
.btn[data-testid="save-recipe-button"]:disabled:hover,
.btn[data-testid="save-recipe-button"].disabled:hover {
  background-color: #218838 !important;
  border-color: #1e7e34 !important;
  opacity: 1 !important;
  cursor: pointer !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
}

button[data-testid="save-recipe-button"]:active,
button[data-testid="save-recipe-button"]:disabled:active,
button[data-testid="save-recipe-button"].disabled:active,
.btn[data-testid="save-recipe-button"]:active,
.btn[data-testid="save-recipe-button"]:disabled:active,
.btn[data-testid="save-recipe-button"].disabled:active {
  background-color: #1e7e34 !important;
  border-color: #1c7430 !important;
  opacity: 1 !important;
  transform: translateY(0) !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

/* SOBRESCRIBIR CUALQUIER ESTILO DE COREUI */
.c-btn[data-testid="save-recipe-button"],
.c-btn[data-testid="save-recipe-button"]:disabled,
.c-btn[data-testid="save-recipe-button"].disabled {
  opacity: 1 !important;
  cursor: pointer !important;
  pointer-events: auto !important;
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  color: white !important;
}

/* FORZAR EN TODOS LOS ESTADOS */
button[data-testid="save-recipe-button"] *,
button[data-testid="save-recipe-button"]:disabled *,
button[data-testid="save-recipe-button"].disabled * {
  pointer-events: auto !important;
  cursor: pointer !important;
}

/* FORZAR BOTÓN CON ATRIBUTO VUE SCOPED */
button[data-v-a66a1710],
button[data-v-a66a1710]:disabled,
button[data-v-a66a1710].disabled,
button[data-v-a66a1710][disabled="disabled"],
button[data-v-a66a1710][disabled="true"],
button[data-v-a66a1710][disabled] {
  opacity: 1 !important;
  cursor: pointer !important;
  pointer-events: auto !important;
  background-color: #28a745 !important;
  border-color: #28a745 !important;
  color: white !important;
  display: inline-block !important;
  text-decoration: none !important;
  user-select: auto !important;
  touch-action: manipulation !important;
  outline: none !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
  filter: none !important;
  transform: none !important;
  transition: all 0.15s ease-in-out !important;
}

button[data-v-a66a1710]:hover,
button[data-v-a66a1710]:disabled:hover,
button[data-v-a66a1710].disabled:hover {
  background-color: #218838 !important;
  border-color: #1e7e34 !important;
  opacity: 1 !important;
  cursor: pointer !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
}

button[data-v-a66a1710]:active,
button[data-v-a66a1710]:disabled:active,
button[data-v-a66a1710].disabled:active {
  background-color: #1e7e34 !important;
  border-color: #1c7430 !important;
  opacity: 1 !important;
  transform: translateY(0) !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}

/* FORZAR EN ELEMENTOS INTERNOS */
button[data-v-a66a1710] *,
button[data-v-a66a1710]:disabled *,
button[data-v-a66a1710].disabled * {
  pointer-events: auto !important;
  cursor: pointer !important;
}

</style>